import { Injectable } from '@angular/core';
import { LocalService } from './storage/local.service';
import { JwtLegFiClaims } from './auth/jwt-legfi-claims.model';
import { LegFiJwtService } from './auth/legfi-jwt.service';

export enum TableTypeNames {
    MEMBERS = 'members',
    UNITS = 'units',
    UNIT_SELECTOR = 'unitSelector',
    TRANSACTIONS = 'transactions',
    TRANSACTIONS_V2 = 'transactionsV2',
    RECONCILIATIONS = 'reconciliations',
    ISSUED_CHARGES = 'issuedCharges',
    AMENITIES = 'amenities',
    AMENITIES_ARCHIVED = 'amenitiesArchived',
    AMENITY_SCHEDULED_EMAILS = 'amenityScheduledEmails',
    PMC_GROUPS = 'propertyManagementGroups',
    PMC_COMMUNITIES = 'propertyManagementCommunities',
    PMC_FORMS = 'propertyManagementRequestForms',
    PMC_FORM_SUBMISSIONS = 'propertyManagementRequestFormSubmissions',
    PMC_VIOLATIONS = 'propertyManagementViolations',
    VIOLATIONS = 'violations',
    PMC_TEAM = 'propertyManagementTeam',
    PMC_FEATURE_FLAGS = 'propertyManagementFeatureFlags',
    PMC_SUBSCRIPTIONS = 'propertyManagementSubscriptions',
    PMC_INVOICES = 'propertyManagementInvoices',
    COMMUNICATION_LOGS = 'communicationLogs',
    COMMUNICATION_LOGS_V2 = 'communicationLogsV2',
    CHECK_REGISTER = 'checkRegister',
    RESALE_DOCUMENT_ORDERS = 'resaleDocumentOrders',
    RESALE_DOCS_ENROLLMENTS = 'resaleDocsEnrollments',
    BANK_RECONCILIATIONS = 'bankReconciliations',
    RECONCILIATION_TRANSACTIONS = 'reconciliationTransactions',
    BOOKKEEPING_ENROLLMENTS = 'bookkeepingEnrollments',
    DRAFT_BUDGETS = 'draftBudgets',
    TASKS = 'tasks',
    CHART_OF_ACCOUNTS_ASSETS = 'chartOfAccountsAssets',
    CHART_OF_ACCOUNTS_LIABILITIES = 'chartOfAccountsLiabilities',
    CHART_OF_ACCOUNTS_EQUITIES = 'chartOfAccountsEquities',
    GENERAL_LEDGER = 'generalLedger',
    ACCOUNT_REGISTER = 'accountRegister',
    DEPOSIT_DETAILS = 'depositDetails',
    SPEND_CARDS = 'spendCards',
    ADMIN_STRIPE_PRICES = 'adminStripePrices',
    ADMIN_STRIPE_PRODUCTS = 'adminStripeProducts',
    ADMIN_STRIPE_COUPONS = 'adminStripeCoupons',
    ADMIN_STRIPE_PAYMENT_METHODS = 'adminStripePaymentMethods',
    ADMIN_STRIPE_SYNC_JOBS = 'adminStripeSyncJobs',
    ADMIN_STRIPE_CUSTOMERS = 'adminStripeCustomers',
    ADMIN_STRIPE_SUBSCRIPTIONS = 'adminStripeSubscriptions',
    ADMIN_STRIPE_INVOICES = 'adminStripeInvoices',
    ADMIN_STRIPE_COUPON_SUBSCRIPTIONS = 'adminStripeCouponSubscriptions',
    ADMIN_STRIPE_CUSTOMER_SUBSCRIPTIONS = 'adminStripeCustomerSubscriptions',
    ADMIN_STRIPE_CUSTOMER_INVOICES = 'adminStripeCustomerInvoices',
    ADMIN_STRIPE_CUSTOMER_PAYMENT_METHODS = 'adminStripeCustomerPaymentMethods',
    ADMIN_STRIPE_PRODUCT_PRICES = 'adminStripeProductPrices',
    ADMIN_STRIPE_PRICE_SUBSCRIPTIONS = 'adminStripePriceSubscriptions',
    ADMIN_STRIPE_SUBSCRIPTION_ITEMS = 'adminStripeSubscriptionItems',
    ADMIN_STRIPE_SUBSCRIPTION_INVOICES = 'adminStripeSubscriptionInvoices',
    ADMIN_STRIPE_ORGANIZATIONS = 'adminStripeOrganizations',
    ADMIN_STRIPE_PMCS = 'adminStripePmcs',
    ADMIN_MAILER_BATCHES = 'adminMailerBatches',
    ADMIN_GEOGRAPHIC_SEARCH = 'adminGeographicSearch',
    ADMIN_GABLE = 'adminGable',
    ADMIN_AI_ACTIVITY = 'adminAiActivity',
    ADMIN_ONBOARDING_SPECIALISTS_ACTIVE = 'adminOnboardingSpecialistsActive',
    ADMIN_ONBOARDING_SPECIALISTS_BDR = 'adminOnboardingSpecialistsBdr',
    // SD-9537 removed ADMIN_ONBOARDING_SPECIALISTS_PAUSED: pause became a per-queue STATUS shown on
    // the row, so the separate paused table it keyed no longer exists. Any column selection a user
    // had stored under the old key is simply never read again.

    FINANCIALS_LOG = 'financialsLog',
    FC_BANK_FEED = 'fcBankFeed',
    PAYMENTS = 'payments',
    BALLOTS = 'ballots',
    BALLOT_SETTINGS_TEMPLATES = 'ballotSettingsTemplates',
    WEIGHTED_VOTING_CATEGORIES = 'weightedVotingCategories',
}

export interface TableColumn {
    key: string;
    title: string;
}

export type StoredTableColumns = {
    [key in TableTypeNames]: string[];
};

@Injectable({
    providedIn: 'root',
})
export class ColumnService
{
    private static get StorageKey() {
        const base = 'payhoa-columns';
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        return `${base}-${jwt.orgId}`;
    }

    public static read(table: TableTypeNames): string[] {
        const columns =  ColumnService.readAll();

        return columns ? columns[table] : null;
    }

    public static clear(table: TableTypeNames): void {
        const columns =  ColumnService.readAll();

        if (columns && columns[table]) {
            delete columns[table];
        }

        ColumnService.storeAll(columns);
    }

    public static store(table: TableTypeNames, data: TableColumn[]): void {
        let columns =  ColumnService.readAll();

        if (!columns) {
            columns = {} as StoredTableColumns;
        }

        columns[table] = data.map((column) => column.key);

        ColumnService.storeAll(columns);
    }

    private static readAll(): StoredTableColumns {
        const data = LocalService.get(ColumnService.StorageKey);

        return JSON.parse(data) as StoredTableColumns;
    }

    private static clearAll(): void {
        LocalService.destroy(ColumnService.StorageKey);
    }

    private static storeAll(data: StoredTableColumns): void {
        LocalService.set(ColumnService.StorageKey, JSON.stringify(data));
    }
}
